import styled from 'styled-components';
import { colors, fontSize, fontWeight, fontFamilyPrimary } from '../../theme';
import { StepStatus } from '../../utils/hooks/usePipeline';

export const PipelineContainerStyled = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 24px 0;
`;

export const StepRowStyled = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    background-color: ${colors.grey000};
    border: 1px solid ${colors.grey600};
    overflow: hidden;
`;

export const StepMainRowStyled = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
`;

interface StepStatusProps {
    status: StepStatus;
}

const iconColorByStatus = (status: StepStatus): string => {
    switch (status) {
        case 'success': return `background-color: ${colors.alertSuccess}; color: ${colors.grey000};`;
        case 'error': return `background-color: ${colors.alertDanger}; color: ${colors.grey000};`;
        case 'running': return 'background-color: transparent;';
        default: return `background-color: ${colors.grey600}; color: transparent;`;
    }
};

const labelColorByStatus = (status: StepStatus): string => {
    switch (status) {
        case 'running':
        case 'success': return `color: ${colors.grey200};`;
        case 'error': return `color: ${colors.alertDanger};`;
        default: return `color: ${colors.grey400};`;
    }
};

export const StepIconCircleStyled = styled.div(({ status }: StepStatusProps) => `
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 12px;
    font-weight: ${fontWeight.bold};
    transition: background-color 300ms ease-in-out;
    ${iconColorByStatus(status)}
`);

export const StepLabelTextStyled = styled.span(({ status }: StepStatusProps) => `
    flex: 1;
    font-family: ${fontFamilyPrimary};
    font-size: ${fontSize.sm};
    font-weight: ${fontWeight.regular};
    transition: color 300ms ease-in-out;
    ${labelColorByStatus(status)}
`);

export const StepErrorPanelStyled = styled.div`
    border-top: 1px solid ${colors.grey600};
    padding: 12px 16px;
    background-color: rgba(255, 67, 56, 0.04);
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const StepErrorTextStyled = styled.pre`
    margin: 0;
    padding: 8px 12px;
    background-color: rgba(255, 67, 56, 0.08);
    border-left: 3px solid ${colors.alertDanger};
    border-radius: 4px;
    font-family: ${fontFamilyPrimary};
    font-size: ${fontSize.s};
    color: ${colors.alertDanger};
    white-space: pre-wrap;
    word-break: break-word;
`;

export const RetryButtonStyled = styled.button`
    align-self: flex-start;
    padding: 7px 14px;
    background-color: ${colors.alertDanger};
    color: ${colors.grey000};
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-family: ${fontFamilyPrimary};
    font-size: ${fontSize.s};
    font-weight: ${fontWeight.semiBold};
    transition: background-color 200ms ease-in-out;

    &:hover {
        background-color: #d9362c;
    }
`;

export const PipelineSuccessMessageStyled = styled.div`
    text-align: center;
    padding: 20px 24px;
    background-color: rgba(34, 197, 94, 0.08);
    border: 1px solid ${colors.alertSuccess};
    border-radius: 8px;
    font-family: ${fontFamilyPrimary};
    font-size: ${fontSize.m};
    font-weight: ${fontWeight.semiBold};
    color: ${colors.alertSuccess};
    margin-top: 8px;
`;

export const PipelineActionsStyled = styled.div`
    display: flex;
    justify-content: flex-start;
    margin-top: 24px;
`;
